/**
 * RunSOPButton — a compact button + popover picker to attach a SOP run to
 * any entity (project / task / item_catalog / client). Uses the global
 * `useSopRunner()` context so the runner panel keeps its home in AppShell.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Play, Search } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useSopRunner } from "@/components/sops/SopRunnerContext";

export default function RunSOPButton({
  linkKind,
  linkId,
  label = "Run SOP",
  size = "sm",
  variant = "outline",
  testKey = "generic",
}) {
  const { activeWorkspaceId } = useAuth();
  const { startRun } = useSopRunner();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/sops`, { params: { scope: "mine" } });
        setRows(data || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load SOPs")); }
    })();
  }, [open, activeWorkspaceId]);

  const filtered = q
    ? rows.filter((r) => (r.title || "").toLowerCase().includes(q.toLowerCase()) || (r.category || "").toLowerCase().includes(q.toLowerCase()))
    : rows;

  const launch = async (sop) => {
    setBusy(true);
    try {
      // Iter 125a — when launching from a Task, pre-populate the
      // time-target so the assignee can toggle timesheet logging on
      // from within the runner without re-selecting a task.
      const extra = { link_kind: linkKind, link_id: linkId };
      if (linkKind === "task" && linkId) extra.time_task_id = linkId;
      const id = await startRun(sop, extra);
      if (id) setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size={size} variant={variant} className="rounded-none" data-testid={`run-sop-btn-${testKey}`}>
          <Play className="w-3.5 h-3.5 mr-1.5" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-none" data-testid={`run-sop-menu-${testKey}`}>
        <div className="p-2 border-b border-border bg-muted/30">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pick a SOP to run…"
              className="pl-7 h-8 rounded-none text-xs"
              data-testid={`run-sop-search-${testKey}`}
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-72 overflow-auto" data-testid={`run-sop-list-${testKey}`}>
          {filtered.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">No SOPs match.</div>
          ) : filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => launch(s)}
              disabled={busy}
              className="w-full text-left px-3 py-2 border-b border-border hover:bg-muted/40 disabled:opacity-50"
              data-testid={`run-sop-pick-${testKey}-${s.id}`}
            >
              <div className="text-sm font-semibold truncate">{s.title}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {s.category ? `${s.category} · ` : ""}{s.version || "v1"}{s.tags?.length ? ` · ${s.tags.slice(0, 3).map((t) => `#${t}`).join(" ")}` : ""}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
